// The MIT License (MIT)
//
// Copyright (c) 2016-2021 Camptocamp SA
//
// Permission is hereby granted, free of charge, to any person obtaining a copy of
// this software and associated documentation files (the "Software"), to deal in
// the Software without restriction, including without limitation the rights to
// use, copy, modify, merge, publish, distribute, sublicense, and/or sell copies of
// the Software, and to permit persons to whom the Software is furnished to do so,
// subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in all
// copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS
// FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR
// COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER
// IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN
// CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.

/**
 * Application entry point.
 *
 * This file includes `import`'s for all the components/directives used
 * by the HTML page and the controller to provide the configuration.
 */

import './sass/desktop.scss';
import './sass/vars_desktop.scss';

import angular from 'angular';
import {isEventUsingCtrlKey} from 'ngeo/utils';
import gmfControllersAbstractDesktopController, {
  AbstractDesktopController,
} from 'gmf/controllers/AbstractDesktopController';
import apronaBase from '../apronamodule';

import ApronaQueryWindow from '../displaywindow/windowComponent.js';
import apronaQualityAggregateModule from '../qualityaggregate/module.js';
import apronaRechargeModule from '../recharge/module.js';
import ngeoMiscToolActivate from 'ngeo/misc/ToolActivate';
import ngeoStatemanagerModule from 'ngeo/statemanager/module';

import {QualityAggregateParam, registerWindowFunctions, createBanDatasource} from '../aprona.js';

/**
 * @private
 */
class Controller extends AbstractDesktopController {
  /**
   * @param {angular.IScope} $scope Scope.
   * @param {angular.auto.IInjectorService} $injector Main injector.
   * @ngInject
   */
  constructor($scope, $injector) {
    super($scope, $injector);

    // Recalculate map size after CSS loading
    $scope.$watch(
      () => this.loading,
      (value) => {
        if (value === false) this.map.updateSize();
      }
    );

    // Custom datasource from BAN
    const gmfSearchOptions = $injector.get('gmfSearchOptions');
    gmfSearchOptions.datasources.push(createBanDatasource($injector, this.map));

    this.apronaUrl = $injector.get('apronaUrl');
    this.ermesUrl = $injector.get('ermesUrl');
    this.emsUrl = $injector.get('emsUrl');
    this.measureAssetsBaseUrl = $injector.get('measureAssetsBaseUrl');
    this.measureSearchUrl = $injector.get('measureSearchUrl');
    this.measureCaptagesUrl = $injector.get('measureCaptagesUrl');
    this.maCommuneUrl = $injector.get('maCommuneUrl');
    this.currentInterfaceUrl = $injector.get('currentInterfaceUrl');

    // Tools panels
    const ngeoStateManager = $injector.get("ngeoStateManager");
    this.qualityAggregatePanelActive = ngeoStateManager.getInitialBooleanValue(QualityAggregateParam.PANEL_ACTIVE);
    this.rechargePanelActive = false;

    // Exclusive mapTools
    const rechargePanelActivate = new ngeoMiscToolActivate(this, 'rechargePanelActive');
    this.ngeoToolActivateMgr.registerTool('mapTools', rechargePanelActivate, false);

    /**
     * @type {?import('ol/geom/Point').default}
     */
    this.rechargePoint = null;

    this.instance =
      window.location.pathname
        .split('/')
        .filter((p) => p)
        .slice(0, 2)
        .indexOf('ermes') >= 0
        ? 'ermes'
        : 'aprona';

    /** @type {angular.gettext.gettextCatalog} */
    const gettextCatalog = $injector.get('gettextCatalog');

    /**
     * @type {string}
     */
    this.lang = gettextCatalog.currentLanguage;

    registerWindowFunctions($injector);
  }

  /**
   * @param {string} lang Language.
   */
  switchLanguageAndRefresh(lang) {
    this.switchLanguage(lang);
    setTimeout(() => location.reload(), 100);
  }

  /**
   * @param {JQuery.Event} event keydown event.
   */
  onKeydown(event) {
    if (event && isEventUsingCtrlKey(event) && event.key === 'p') {
      this.printPanelActive = true;
      event.preventDefault();
    }
  }
}

/**
 * @hidden
 */
const apronaModule = angular.module('Appdesktop', [
  apronaBase.name,
  gmfControllersAbstractDesktopController.name,
  ApronaQueryWindow.name,
  apronaQualityAggregateModule.name,
  apronaRechargeModule.name,
  ngeoStatemanagerModule.name,
]);

apronaModule.value('gmfContextualdatacontentTemplateUrl', 'gmf/contextualdata');
apronaModule.run(
  /**
   * @ngInject
   * @param {angular.ITemplateCacheService} $templateCache
   */
  ($templateCache) => {
    // @ts-ignore: webpack
    $templateCache.put('gmf/contextualdata', require('./contextualdata.html'));
  }
);

apronaModule.controller('DesktopController', Controller);

export default apronaModule;
